/**
 * API Support methods for LoaderNormal
 * These methods are attached to LoaderNormal.prototype in the main file
 */

import { JsonUtility } from '../../app/jsonUtility.js';
import { JsonObject } from '../../app/json/jsonObject.js';
import { AppSiteTemplateData } from '../../model/appSiteTemplateData.js';
import type { LoaderNormal } from './loaderNormal.js';


/**
 * API Support helper methods for LoaderNormal (private methods only)
 * The public getTemplatesApiResponse method is in loaderNormal_ILoader.ts
 */
export const apiMethods = {
    /**
     * Gets all templates as dictionary of AppSite to template data
     * Returns per-AppSite template information for API responses
     * When includeSearchAppSites is true, includes templates from both main AppSite and SearchAppSites
     * When includeSearchAppSites is false, only includes main AppSite templates (for testing comparison)
     */
    _getAllTemplatesJson(this: LoaderNormal, includeSearchAppSites: boolean = true, orderedTemplateNames: string[] | null = null): Record<string, AppSiteTemplateData> {
        const result: Record<string, AppSiteTemplateData> = {};

        // Build list of AppSites to include (main first, then SearchAppSites)
        const searchAppSites = includeSearchAppSites ? this.getSearchAppSitesForAppSite(this._appSite) : '';
        const appSitesToInclude = (this.constructor as typeof LoaderNormal).BuildAppSitesToLoad(this._appSite, searchAppSites);

        // Build AppSiteTemplateData for each AppSite in order
        for (const appSite of appSitesToInclude) {
            if (!this._store.hasAppSite(appSite)) {
                continue;
            }
            const templatesJson = this._buildTemplatesJsonForAppSite(appSite, orderedTemplateNames);

            result[appSite] = new AppSiteTemplateData(
                appSite,
                templatesJson,
                null, // No metadata for Normal loader
                this.getAppFile(appSite, '').appFile
            );
        }

        return result;
    },

    /**
     * Builds templates JSON for a single AppSite (html + json)
     * When orderedTemplateNames is provided, templates are ordered according to that list (processing order)
     * @private
     */
    _buildTemplatesJsonForAppSite(this: LoaderNormal, appSite: string, orderedTemplateNames: string[] | null = null): string {
        const cached = this._store.tryGetTemplates(appSite);
        if (!cached) return '{}';

        const templateData: Record<string, { html: string | null; json: JsonObject | null }> = {};

        // Collect templates for this AppSite
        for (const [templateKey, template] of cached.entries()) {
            templateData[templateKey] = {
                html: template.html || null,
                json: template.json || null
            };
        }

        // Build list of template keys in correct order
        let templateKeys: string[];
        const resolvedNames = new Map<string, string>();
        if (orderedTemplateNames && orderedTemplateNames.length > 0) {
            // orderedTemplateNames is the SET as well as the order: it is the engine's merge closure.
            templateKeys = [];
            const addedKeys = new Set<string>();
            const allKeys = Array.from(cached.keys());

            for (const name of orderedTemplateNames) {
                const componentName = name.startsWith('!') ? name.slice(1) : name;
                const componentLower = componentName.toLowerCase();
                for (const key of allKeys) {
                    if (addedKeys.has(key)) continue;
                    const keyLower = key.toLowerCase();
                    if (keyLower === componentLower || keyLower.endsWith(`_${componentLower}`)) {
                        templateKeys.push(key);
                        addedKeys.add(key);
                        const appSitePrefix = `${appSite.toLowerCase()}_`;
                        resolvedNames.set(key, componentLower.startsWith(appSitePrefix)
                            ? componentLower.slice(appSitePrefix.length)
                            : componentLower);
                        break;
                    }
                }
            }
        } else {
            // Use natural insertion order from Map
            templateKeys = Array.from(cached.keys());
        }

        const orderedResult: Record<string, { html: string | null; json: JsonObject | null }> = {};
        for (const key of templateKeys) {
            const entry = templateData[key];
            const resolvedName = resolvedNames.get(key);
            if (resolvedName !== undefined) {
                // The closure merge has already run every server-owned handler. Ship that exact resolved
                // DataJson to the client tier; serializing the raw disk JSON here would reintroduce
                // placeholders such as {{DynamicData}}, which the browser cannot and must not execute
                // server-side. resolvedTemplateJson is keyed "{mergeAppSite}_{template}" lower-cased
                // (loaderNormal_Inheritance takes the MERGE appSite, the owning one arriving separately as
                // resolvedAppSite), so a template pulled in from another AppSite by a gated redirect is
                // stored under the merge AppSite -- recompose against this._appSite, not against appSite.
                const resolvedJson = this._dynamicResults.resolvedTemplateJson
                    .get(`${this._appSite.toLowerCase()}_${resolvedName}`).asObject();
                if (resolvedJson) {
                    orderedResult[key] = { html: entry.html, json: resolvedJson };
                    continue;
                }
            }
            orderedResult[key] = entry;
        }

        return JsonUtility.serializeObject(orderedResult);
    }
};

/**
 * Static methods to be attached to LoaderNormal class
 */
export const apiStaticMethods = {
    /**
     * Escapes special characters in a string for JSON serialization
     * Matches C# EscapeJson implementation - uses \u0022 for double quotes
     * @private
     */
    _escapeJsonString(value: string): string {
        if (!value) return '';
        return value
            .replace(/\\/g, '\\\\')
            .replace(/"/g, '\\u0022')
            .replace(/\n/g, '\\n')
            .replace(/\r/g, '\\r')
            .replace(/\t/g, '\\t')
            .replace(/\x08/g, '\\b')  // Backspace char (0x08), NOT regex \b word boundary
            .replace(/\f/g, '\\f');
    }
};
